import { cva } from "class-variance-authority";
import { FiSearch } from "react-icons/fi";
import { cn } from "../../../client/cn";

export interface SearchFieldProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "size" | "className"
  > {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  inputClassName?: string;
  iconClassName?: string;
  invalid?: boolean;
  busy?: boolean;
}

const searchInputVariants = cva(
  "w-full rounded-full border px-4 py-3 pl-10 text-neutral-700 shadow-xs transition-colors focus:outline-hidden dark:bg-neutral-800 dark:text-white",
  {
    variants: {
      state: {
        default:
          "bg-white border-neutral-200 dark:border-neutral-700 focus:border-primary focus:ring-2 focus:ring-primary",
        invalid:
          "bg-white border-danger text-danger focus:border-danger focus:ring-2 focus:ring-danger dark:border-danger dark:bg-neutral-800 dark:text-danger-300",
        disabled:
          "cursor-not-allowed border-neutral-200 bg-neutral-100 text-neutral-400 dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-500",
      },
      busy: {
        true: "pr-11",
        false: "",
      },
    },
    defaultVariants: {
      state: "default",
      busy: false,
    },
  },
);

const iconVariants = cva("absolute left-3 top-1/2 -translate-y-1/2 text-lg", {
  variants: {
    state: {
      default: "text-neutral-400",
      invalid: "text-danger",
      disabled: "text-neutral-300 dark:text-neutral-500",
    },
  },
  defaultVariants: {
    state: "default",
  },
});

export function SearchField({
  value,
  onChange,
  placeholder = "Search...",
  className = "",
  inputClassName = "",
  iconClassName = "",
  invalid = false,
  busy = false,
  disabled = false,
  type = "text",
  ...props
}: SearchFieldProps) {
  const state = disabled ? "disabled" : invalid ? "invalid" : "default";

  return (
    <div className={cn("relative", className)}>
      <input
        {...props}
        type={type}
        value={value}
        onChange={(event) => onChange(event.currentTarget.value)}
        placeholder={placeholder}
        className={cn(searchInputVariants({ state, busy }), inputClassName)}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-busy={busy || undefined}
      />
      <FiSearch
        className={cn(iconVariants({ state }), iconClassName)}
        aria-hidden="true"
      />
    </div>
  );
}
